import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Download, Loader2, UserSearch, UserPlus } from "lucide-react";

const K = ({ l, v, t, c = "" }) => <div className={`rounded border p-2 ${c}`} data-testid={t}><div className="text-[10px] uppercase text-slate-500">{l}</div><div className="font-black text-lg">{v ?? 0}</div></div>;
const isRun = (j) => ["pending", "running"].includes(j?.status);
const reason = (x) => x.status === "conferir_cpf_invalido" ? "CPF inválido" : (x.possiveis_correspondencias || []).map((m) => `${m.nome} (${m.motivo})`).join(", ");

const downloadCsv = async (f) => {
  const { data } = await api.get(`/integrations/nextfit/finance-audit/csv/${f}`, { responseType: "blob" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = f; a.click();
};

const ReviewList = ({ rows, prefix }) => !!rows.length && (
  <div className="rounded border border-amber-300 bg-amber-50 p-2 max-h-56 overflow-auto" data-testid={`${prefix}-review-list`}>
    <div className="font-bold mb-1">Precisam de revisão — NÃO serão importados ({rows.length})</div>
    {rows.slice(0, 200).map((x) => <div key={x.nextfit_client_code} className="border-t py-1">#{x.nextfit_client_code} · {x.nome} · {x.situacao_nextfit} · fin. {x.financeiro_nextfit} · {reason(x)}</div>)}
  </div>
);

const ExecBox = ({ n, prefix, label, disabled, onRun }) => {
  const [confirm, setConfirm] = useState("");
  if (!n) return null;
  return (
    <div className="rounded border border-emerald-300 bg-emerald-50 p-3 flex items-center gap-2 flex-wrap" data-testid={`${prefix}-exec-box`}>
      <span className="font-bold text-emerald-900">{label} {n} aluno(s):</span>
      <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="digite IMPORTAR" className="h-8 w-40 bg-white" data-testid={`${prefix}-confirm-input`}/>
      <Button size="sm" disabled={confirm.trim().toUpperCase() !== "IMPORTAR" || disabled} onClick={async () => { await onRun(confirm); setConfirm(""); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`${prefix}-exec-btn`}><UserPlus className="w-4 h-4 mr-1"/>Importar</Button>
    </div>
  );
};

const ExecResult = ({ ex, prefix }) => (
  <>
    {ex?.status === "done" && <div className="mt-3 text-xs font-bold text-emerald-800" data-testid={`${prefix}-exec-result`}>Importação concluída: {ex.result?.criados} criados ({ex.result?.ativos} ativos) · {ex.result?.ja_existiam} já existiam · lote {String(ex.result?.batch || "").slice(0, 8)} · backup {ex.result?.backup || "—"}</div>}
    {ex?.status === "error" && <div className="mt-2 text-xs text-rose-700" data-testid={`${prefix}-exec-error`}>Erro na importação: {ex.error}</div>}
  </>
);

const PriorityPanel = ({ p, ex, busy, running, onPreview, onExec }) => {
  const r = p?.result || {}, rows = p?.rows || [];
  const imp = rows.filter((x) => x.status === "ausente");
  const rev = rows.filter((x) => x.status.startsWith("conferir"));
  return (
    <div className="space-y-3 text-xs">
      <div className="flex gap-2 flex-wrap">
        <Button onClick={onPreview} disabled={busy || running} className="bg-rose-600 hover:bg-rose-700" data-testid="nocpf-priority-preview-btn">{isRun(p) ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <UserSearch className="w-4 h-4 mr-1"/>}Gerar prévia prioritária</Button>
        {p?.csv && <Button variant="outline" onClick={() => downloadCsv(p.csv)} data-testid="nocpf-priority-csv-btn"><Download className="w-4 h-4 mr-1"/>Lista (CSV)</Button>}
      </div>
      <p className="text-slate-500">Seleciona somente alunos ausentes no American Fit com contrato ativo, suspensos ou com mensalidade vencida — sem CPF ou com CPF inválido (o CPF fica vazio). Alunos antigos sem contrato ou encerrados fora desses grupos não entram. A prévia não grava nada.</p>
      {isRun(p) && <div className="font-bold text-sky-700" data-testid="nocpf-priority-running">Em andamento: {p.progress?.phase || "..."}</div>}
      {p?.status === "error" && <div className="text-rose-700" data-testid="nocpf-priority-error">Erro na prévia: {p.error}</div>}
      {!p?.status && <div className="text-slate-500" data-testid="nocpf-priority-empty">Nenhuma prévia prioritária gerada ainda.</div>}
      {p?.status === "done" && (
        <div className="space-y-3" data-testid="nocpf-priority-report">
          <div className="font-bold" data-testid="nocpf-priority-mode">PRÉVIA PRIORITÁRIA · gerada {String(r.generated_at || "").slice(0, 16).replace("T", " ")} UTC · nada gravado</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <K l="Alunos únicos selecionados" v={r.pessoas_unicas} t="nocpf-priority-unique"/>
            <K l="Já existem no American Fit" v={r.ja_existentes_no_af} t="nocpf-priority-existing"/>
            <K l="Precisam de revisão" v={r.conferencia_manual} t="nocpf-priority-review" c="border-amber-300"/>
            <K l="Podem ser importados" v={r.ausentes} t="nocpf-priority-importable" c="border-emerald-400"/>
          </div>
          {r.grupos && <div className="text-slate-600" data-testid="nocpf-priority-groups">Sem CPF: {r.grupos.vazio_contrato_ativo} contrato ativo · {r.grupos.vazio_suspenso} suspensos · {r.grupos.vazio_mensalidade_vencida} mensalidade vencida — CPF inválido: {r.grupos.invalido_contrato_ativo} contrato ativo · {r.grupos.invalido_suspenso} suspensos · {r.grupos.invalido_mensalidade_vencida} mensalidade vencida (um aluno pode estar em mais de um grupo)</div>}
          <div className="text-slate-600" data-testid="nocpf-priority-will-be">Dos importáveis: {r.ausentes_ativos} ficarão ativos · {r.ausentes_suspensos} suspensos · {r.ausentes_inativos} inativos (mensalidade vencida){r.ausentes_plano_sem_correspondencia ? ` · ${r.ausentes_plano_sem_correspondencia} sem plano correspondente (entram sem plano)` : ""}</div>
          <ReviewList rows={rev} prefix="nocpf-priority"/>
          {!!imp.length && (
            <div className="rounded border p-2 max-h-72 overflow-auto" data-testid="nocpf-priority-import-list">
              <div className="font-bold mb-1">Serão importados ({imp.length})</div>
              {imp.map((x) => <div key={x.nextfit_client_code} className="border-t py-1" data-testid="nocpf-priority-import-row">#{x.nextfit_client_code} · <b>{x.nome}</b> · CPF {x.tipo_cpf} · {x.situacao_nextfit} · fin. {x.financeiro_nextfit} · ficará <b>{x.ficara}</b> · {x.plano_af || <span className="text-amber-700">sem plano</span>} · vence {String(x.vencimento || "—").slice(0, 10)}</div>)}
            </div>
          )}
          <ExecBox n={r.ausentes} prefix="nocpf-priority" label="Importação prioritária:" disabled={busy || running} onRun={onExec}/>
        </div>
      )}
      <ExecResult ex={ex} prefix="nocpf-priority"/>
    </div>
  );
};

const GeneralPanel = ({ p, ex, busy, running, onPreview, onExec }) => {
  const r = p?.result || {}, isGeneral = p?.status === "done" && !r.priority;
  return (
    <div className="space-y-3 text-xs">
      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" onClick={onPreview} disabled={busy || running} data-testid="nocpf-preview-btn">{isRun(p) ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <UserSearch className="w-4 h-4 mr-1"/>}Gerar prévia geral</Button>
        {isGeneral && p.csv && <Button variant="outline" onClick={() => downloadCsv(p.csv)} data-testid="nocpf-csv-btn"><Download className="w-4 h-4 mr-1"/>Lista (CSV)</Button>}
      </div>
      <p className="text-slate-500">Todos os alunos sem CPF do Next Fit, inclusive antigos e encerrados. A prévia não grava nada.</p>
      {isRun(p) && <div className="font-bold text-sky-700" data-testid="nocpf-running">Em andamento: {p.progress?.phase || "..."}</div>}
      {p?.status === "error" && <div className="text-rose-700" data-testid="nocpf-error">Erro na prévia: {p.error}</div>}
      {isGeneral && (
        <div className="space-y-3" data-testid="nocpf-report">
          <div className="font-bold">Prévia geral · gerada {String(r.generated_at || "").slice(0, 16).replace("T", " ")} UTC · nada gravado</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <K l="Sem CPF no Next Fit" v={r.sem_cpf_no_nextfit} t="nocpf-total"/>
            <K l="CPF inválido no Next Fit" v={r.cpf_invalido_no_nextfit} t="nocpf-invalid"/>
            <K l="Já existem no American Fit" v={r.ja_existentes_no_af} t="nocpf-existing"/>
            <K l="Ausentes (serão importados)" v={r.ausentes} t="nocpf-missing"/>
            <K l="Ausentes ativos" v={r.ausentes_ativos} t="nocpf-active"/>
            <K l="Ausentes suspensos" v={r.ausentes_suspensos} t="nocpf-suspended"/>
            <K l="Ausentes inativos" v={r.ausentes_inativos} t="nocpf-inactive"/>
            <K l="Conferência manual" v={r.conferencia_manual} t="nocpf-review"/>
          </div>
          <ExecBox n={r.ausentes} prefix="nocpf" label="Importação geral:" disabled={busy || running} onRun={onExec}/>
        </div>
      )}
      <ExecResult ex={ex} prefix="nocpf"/>
    </div>
  );
};

export const NextFitNoCpfImport = ({ unitId }) => {
  const [st, setSt] = useState({});
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("priority");
  const timer = useRef(null);
  const load = async () => {
    try {
      const { data } = await api.get(`/integrations/nextfit/units/${unitId}/nocpf`);
      setSt(data);
      clearTimeout(timer.current);
      if ([data.preview, data.execution, data.priority_preview, data.priority_execution].some(isRun)) timer.current = setTimeout(load, 3000);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [unitId]);
  const call = async (url, body, msg) => {
    setBusy(true);
    try { await api.post(`/integrations/nextfit/units/${unitId}/nocpf/${url}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
    finally { setBusy(false); }
  };
  const running = [st.preview, st.execution, st.priority_preview, st.priority_execution].some(isRun);
  return (
    <Card className="p-5" data-testid="nocpf-card">
      <div className="flex items-center gap-2 mb-1"><UserSearch className="w-5 h-5 text-slate-700"/><h3 className="font-heading text-lg font-black text-slate-900">Alunos sem CPF que ficaram de fora</h3></div>
      <p className="text-xs text-slate-500 mb-3">A importação cria só os alunos comprovadamente ausentes, com CPF vazio, sem mexer em cadastros, vendas, contratos atuais ou caixa.</p>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="nocpf-tabs">
          <TabsTrigger value="priority" data-testid="nocpf-tab-priority">Importação prioritária</TabsTrigger>
          <TabsTrigger value="general" data-testid="nocpf-tab-general">Prévia geral</TabsTrigger>
        </TabsList>
        <TabsContent value="priority" className="pt-3">
          {tab === "priority" && <PriorityPanel p={st.priority_preview} ex={st.priority_execution} busy={busy} running={running}
            onPreview={() => call("preview?priority=true", undefined, "Prévia prioritária iniciada (somente leitura)")}
            onExec={(confirm) => call("priority/execute", { confirm }, "Importação prioritária iniciada")}/>}
        </TabsContent>
        <TabsContent value="general" className="pt-3">
          {tab === "general" && <GeneralPanel p={st.preview} ex={st.execution} busy={busy} running={running}
            onPreview={() => call("preview", undefined, "Prévia geral iniciada (somente leitura)")}
            onExec={(confirm) => call("execute", { confirm }, "Importação geral iniciada")}/>}
        </TabsContent>
      </Tabs>
    </Card>
  );
};
